import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Overline, EASE } from "@/components/Motion";
import { GALLERY } from "@/data/content";

const Gallery = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" className="py-28 sm:py-36" data-testid="gallery-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <Reveal>
          <Overline className="text-wine">Moments & Plates</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 mb-16 lg:mb-20">
            The <span className="italic text-wine">Gallery</span>
          </h2>
        </Reveal>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
          {GALLERY.map((item, i) => (
            <Reveal key={item.label} delay={(i % 3) * 0.1} className="mb-6 break-inside-avoid">
              <button
                onClick={() => setActive(i)}
                className="group relative block w-full overflow-hidden rounded-[1.5rem] text-left"
                data-testid={`gallery-item-${i}`}
                aria-label={`Open photo: ${item.label}`}
              >
                <img
                  src={item.img}
                  alt={item.label}
                  loading="lazy"
                  className={`img-lux w-full ${item.aspect} object-cover`}
                />
                <div className="absolute inset-0 bg-wine/0 group-hover:bg-wine/25 transition-colors duration-500" />
                <span className="absolute bottom-5 left-5 text-cream text-xs tracking-[0.25em] uppercase opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  {item.label}
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 text-center">
            <Link
              to="/gallery"
              data-testid="view-full-gallery-btn"
              className="inline-block rounded-full border border-wine px-10 py-4 text-sm tracking-[0.16em] uppercase text-wine hover:bg-wine hover:text-cream transition-colors duration-300"
            >
              View the Full Gallery
            </Link>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-[110] bg-ink/92 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-cream/80 hover:text-cream p-2"
              onClick={() => setActive(null)}
              aria-label="Close photo"
              data-testid="lightbox-close-btn"
            >
              <X size={30} strokeWidth={1.2} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[active].img}
                alt={GALLERY[active].label}
                className="w-full max-h-[80vh] object-contain rounded-2xl"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-4 text-center text-cream/70 text-xs tracking-[0.3em] uppercase">
                {GALLERY[active].label}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
